import type {
  AssetPlacementType,
  FundinfoPricingFrequency,
  GetInstrumentCountriesStatusResponseOne,
  InstrumentBrokerStatus,
  InstrumentDocumentType,
  InstrumentMutualFundLegalStatus,
  StructuredProductType,
} from '@Common/types/bo/instruments';
import type { LegalEntity } from '@Common/types/bo/users/users';
import type {
  B2bInstrumentAssetType,
  InstrumentAssetType,
  InstrumentManagementType,
  InstrumentMutualFundAssetClass,
  SharesStatus,
} from '@Common/types/instruments/instrument';

const countriesStatusFields: Record<keyof GetInstrumentCountriesStatusResponseOne, string> = {
  id: 'Country code',
  name: 'Name',
  entity: 'Entity',
  shares_status: 'Shares status',
  instrument_id: 'Instrument ID',
};

const countriesStatusEntities: Record<LegalEntity, string> = {
  sal: 'SAL',
  sfa: 'SFA',
  sda: 'SDA',
};

const countriesStatusSharesStatus: Record<SharesStatus, string> = {
  disabled: 'Disabled',
  sell_only: 'Sell only',
  suspended: 'Suspended',
  tradable: 'Tradable',
};

export const instrumentsTranslations = {
  instruments: {
    title: 'Instruments',
    name: 'Instrument |||| Instruments',
    tabs: {
      details: 'Details',
      documents: 'Documents',
      cutoff_dates: 'Cutoff dates',
      schedule: 'Schedule',
    },
    fields: {
      id: 'Instrument ID',
      symbol: 'Symbol',
      name: 'Name',
      exchange: 'Exchange',
      status: 'Broker Status',
      class: 'Class',
      tradable: 'Tradable',
      fractionable: 'Fractionable',
      about: 'About (From Provider)',
      about_en: 'About (English override)',
      about_fr: 'About (French)',
      about_pl: 'About (Polish)',
      short_name: 'Short Name',
      short_description: 'Short Description',
      is_enabled: 'Enabled',
      created_at: 'Created',
      from_created_at: 'Created After',
      to_created_at: 'Created Before',
      reason: 'Reason for edition',
      logo_path: 'Logo',
      isin: 'ISIN',
      is_price_fetched: 'Data fetch',
      country_codes_tradable: 'Tradable countries',
      country_codes_sell_only: 'Sell-Only countries',
      country_codes_suspended: 'Suspended countries',
      country_codes_disabled: 'Disabled countries',
      risk: 'Risk',
      support_code: 'Support code',
      mutual_fund_asset_class: 'Asset class',
      management_company: 'Management company',
      kid_urls: 'KID Documents',
      prospectus_url: 'Prospectus document',
      management_fee_pct: 'Management fee (%)',
      management_type: 'Management type',
      five_year_beta: 'Five Year Beta',
      five_year_beta_fallback: 'Five Year Beta Fallback',
      is_adr: 'Is ADR',
      shares_ca_wht_rate_at_source: 'Shares CA WHT rate at source',
      type: 'Type',
      country_code: 'Country code',
      is_halted: 'Is halted',
      asset_type: 'Asset type',
      settlement_location: 'Settlement location',
      sub_custodian_account_number: 'Sub custodian account number',
      has_pending_orders: 'Has pending orders',
      is_complex: 'Is complex',
      is_ftt_rate_zero_override: 'Is FTT rate set to 0',
      intermediate_instrument_parent_id:
        'Referenced instrument ID (Upvest Intermediate Distribution CA)',
      has_open_positions: 'Has open positions',
      data_fetch_completed: 'Data fetch completed',
      country_settings: 'Country settings',
      is_pea_eligible: 'Is PEA eligible',
      legal_status: 'Legal Status',
      is_etf: 'Is ETF',
      is_triggered_arbitrage_allowed: 'Is triggered arbitrage allowed',
      is_single_underlying_asset: 'Is single underlying asset',
      is_withdrawal_trigger_threshold_enabled: 'Is withdrawal trigger threshold enabled',
      is_floor_nav_enabled: 'Is floor NAV enabled',
      is_floor_price_enabled: 'Is floor price enabled',
      marketing_date_start_at: 'Marketing Start Date',
      marketing_date_end_at: 'Marketing End Date',
      maturity_date: 'Maturity Date',
      last_nav_date: 'Last NAV Date',
      last_nav: 'Last NAV',
      nav_cut_off_buy: 'TCC reception cut-off Buy',
      nav_cut_off_sell: 'TCC reception cut-off Sell',
      admin_cut_off_buy: 'Oddo send time Buy',
      admin_cut_off_sell: 'Oddo send time Sell',
      announcement_delay_buy: 'Announcement delay Buy (business days)',
      announcement_delay_sell: 'Announcement delay Sell (business days)',
      pricing_frequency: 'NAV Frequency',
      performance: 'Performance',
      issue_date_at: 'Issue date',
      strike_date_at: 'Strike date',
      structured_product_type: 'Structured product type',
      issue_price_denomination_rate: 'Denomination rate (%)',
      wm_company_ids: 'WM Companies IDs',
      issue_price_denomination_value: 'Denomination value',
      minimum_trading_size: 'Minimum trading size',
      placement_type: 'Placement type',
      commission_to_third_parties_percentage_rate: 'Commission to third parties (%)',
      one_off_entry_cost_percentage_rate: 'One-off entry cost (%)',
      running_cost_percentage_rate: 'Running cost (%)',
      custom_execution_flow: 'Custom execution flow',
      entry_fees_non_acquired_percentage_rate: 'Max non acquired entry fee (%)',
      rebate_fee_rate: 'Rebate fee rate (%)',
      gross_ongoing_costs_ex_ante_rate: 'Gross ongoing costs ex-ante (%)',
      transaction_costs_ex_ante_rate: 'Transaction costs ex-ante (%)',
      incidental_costs_ex_ante_rate: 'Incidental costs ex-ante (%)',
      ccb_last_vl_date: 'Last valuation date',
      has_close_price: 'Close price exists',
      qty_decimal_places: 'Quantity decimal places',
      redemption_period_start_at: 'Redemption start',
      redemption_period_end_at: 'Redemption end',
      maturity_target_instrument_id: 'Maturity target instrument',
    },
    tooltips: {
      qty_decimal_places:
        "The maximum number of decimal places allowed for fractional quantities of this instrument. This must exactly match the broker's supported precision.",
      nav_cut_off:
        'Time by which the TCC must receive an order for it to count as received that day. Not the depositary / PER assurantiel cut-off.',
      admin_cut_off:
        'Time the batch is sent to Oddo. Also the deadline for approving orders of that batch.',
      announcement_delay:
        'Business days between the order reception day (TCC reception cut-off) and the day it is announced to Oddo. 0 announces it on the reception day.',
    },
    mutual_fund_asset_class: {
      diversified: 'Diversified',
      equity: 'Equity',
      bond: 'Bond',
      money_market: 'Money Market',
      real_estate: 'Real Estate',
    } satisfies Record<InstrumentMutualFundAssetClass, string>,
    status: {
      active: 'Active',
      inactive: 'Inactive',
    } satisfies Record<InstrumentBrokerStatus, string>,
    pricing_frequency: {
      daily: 'Daily',
      weekly: 'Weekly',
      biweekly: 'Biweekly',
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      semi_annual: 'Semi-Annual',
      annual: 'Annual',
      semi_monthly: 'Semi-Monthly',
      bi_monthly: 'Bi-Monthly',
      ongoing: 'Ongoing',
    } satisfies Record<FundinfoPricingFrequency, string>,
    legal_status: {
      FCPE: 'FCPE',
      FCP: 'FCP',
      SICAV: 'SICAV',
      SPPICAV: 'SPPICAV',
      MONETAIRE: 'MONETAIRE',
      SICAF: 'SICAF',
      FCIMT: 'FCIMT',
      PIF: 'PIF',
      FCPI: 'FCPI',
      CIPF: 'CIPF',
      structured_products: 'structured_products',
      euro_fund_life_insurance_scheme: 'euro_fund_life_insurance_scheme',
      FCPR: 'FCPR',
      unit_trust: 'unit_trust',
      OEIC: 'OEIC',
      ICVC: 'ICVC',
      OPCI: 'OPCI',
      scpi_real_estate: 'scpi_real_estate',
      ICAV: 'ICAV',
      FPVG: 'FPVG',
      FIAR: 'FIAR',
      FPCI: 'FPCI',
      open_ended_umbrella_unit_trust: 'open_ended_umbrella_unit_trust',
      cayman_ltd: 'cayman_ltd',
      LLC: 'LLC',
      SCI: 'SCI',
      FPS: 'FPS',
      FIC: 'FIC',
      OFS: 'OFS',
      FFS: 'FFS',
      SCA: 'SCA',
      FCC: 'FCC',
      SCP: 'SCP',
      ELTIF: 'ELTIF',
      internal_fund: 'internal_fund',
      SC: 'SC',
      ETC: 'ETC',
      FIP: 'FIP',
      EMTN: 'EMTN',
      AD: 'AD',
      ACTION: 'ACTION',
      DIVERS: 'DIVERS',
    } satisfies Record<InstrumentMutualFundLegalStatus, string>,
    management_type: {
      active: 'Active',
      passive: 'Passive',
    } satisfies Record<InstrumentManagementType, string>,
    asset_type: {
      stock: 'Stock',
      crypto: 'Crypto',
      etf: 'ETF',
      'mutual-fund': 'Mutual Fund',
      'euro-fund': 'Euro Fund',
      commodity: 'Commodity',
      eltif: 'ELTIF',
      'structured-product': 'Structured Product',
      ccb: 'CCB',
    } satisfies Record<InstrumentAssetType | B2bInstrumentAssetType, string>,
    performance: {
      position_3m: '3 months',
      position_6m: '6 months',
      position_1y: '1 year',
      position_3y: '3 years',
    },
    performance_short: {
      position_3m: '3M',
      position_6m: '6M',
      position_1y: '1Y',
      position_3y: '3Y',
    },
    structured_product_type: {
      autocall: 'Autocall',
      phoenix: 'Phoenix',
      reverse_convertible: 'Reverse convertible',
      capital_guaranteed: 'Capital guaranteed',
      cln: 'CLN',
      amc: 'AMC',
      other: 'Other',
    } satisfies Record<StructuredProductType, string>,
    placement_type: {
      public: 'Public',
      private: 'Private',
    } satisfies Record<AssetPlacementType, string>,
    actions: {
      save: {
        title: 'Edit instrument',
        success: 'Instrument successfully saved.',
        failure: 'Error while saving instrument: %{error}',
        failure_logo: 'Error while saving logo: %{error}',
      },
      drop_logo_file: 'Drop your file here',
      confirmation: {
        enabled: 'Are you sure you want to enable this instrument ?',
        metadata_not_fetched: 'Instrument metadata has not been fetched yet or it is incomplete',
        no_beta: 'Instrument does not have a 5 year Beta value',
        disabled: 'Are you sure you want to disable this instrument ?',
        fetch:
          'Are you sure you want change Data Fetch status ? It can take up to 24 hours to be fetched.',
        default: 'Are you sure you want to edit this instrument ?',
      },
    },
    sections: {
      statuses: 'Statuses',
      documents: 'Documents',
      other: 'Other',
      cut_offs: 'Cut-offs',
    },
    cutoff_dates: {
      title: 'Cutoff dates',
      empty: 'No cutoff dates',
      labels: {
        cutoff_date: 'Cutoff date',
      },
      modal: {
        title: 'Edit cutoff calendar',
        add_label: 'New cutoff date',
        add: 'Add',
        staged_additions: 'Dates to be added on save',
        min_one_hint: 'Past dates cannot be changed and at least one upcoming date must remain.',
        success: 'Cutoff dates successfully updated.',
        failure: 'Failed to update cutoff dates: %{error}',
      },
    },
    documents: {
      kid: 'KID',
      prospectus: 'Prospectus',
      complexity_and_target_market_assessment: 'Complexity and Target Market Assessment',
      brochure: 'Brochure',
      term_sheet: 'Terms sheet',
      empty: 'No documents of type',
      not_available: 'No documents available for this instrument type',
      create: {
        title: 'Upload document',
        button: 'Upload document',
        success: 'Document successfully uploaded.',
        failure: 'Error while uploading document',
        choices: {
          kid: 'KID',
          prospectus: 'Prospectus',
          complexity_and_target_market_assessment: 'Complexity and Target Market Assessment',
          brochure: 'Brochure',
          term_sheet: 'Term sheet',
        } satisfies Record<InstrumentDocumentType, string>,
      },
      labels: {
        is_archived: 'Archived',
        created_at: 'Created at',
      },
    },
    create: {
      title: 'Create instrument',
      steps: {
        1: 'General details',
        2: 'Specific',
        3: 'Availability',
      },
      sections: {
        details: 'Details',
        logo: 'Logo',
        structured_products: 'Structured Product details',
        marketing_period: 'Marketing period',
        trading_details: 'Trading details',
        availability: 'Availability',
      },
      actions: {
        success: 'Instrument successfully created.',
        failure: 'Error while creating instrument: %{error}',
      },
      labels: {
        add_wm_companies: 'Add WM Companies',
      },
    },
  },

  'instruments/countries_status': {
    title: 'Country Settings',
    set_all: 'Set all shares status to',
    fields: countriesStatusFields,
    entities: countriesStatusEntities,
    shares_status: countriesStatusSharesStatus,
  },

  'instruments/documents': {
    delete_confirm: {
      title: 'Delete document',
      content: 'Are you sure you want to delete this document? This action cannot be undone.',
    },
    notifications: {
      deleted: 'Document successfully deleted.',
    },
  },

  'instruments/scheduled_edits': {
    title: 'Scheduled Edits',
    empty: 'No scheduled edits for this instrument',
    fields: {
      id: 'ID',
      instrument_id: 'Instrument ID',
      type: 'Type',
      scheduled_at: 'Scheduled at',
      status: 'Status',
      created_at: 'Created at',
      updated_at: 'Updated at',
      actions: 'Actions',
    },
    types: {
      mark_suspendable: 'Mark suspended',
      mark_tradable: 'Mark tradable',
    },
    statuses: {
      pending: 'Pending',
      triggered: 'Triggered',
      cancelled: 'Cancelled',
    },
    create: {
      title: 'Schedule an instrument edit',
      button: 'Schedule edit',
      success: 'Scheduled edit created',
      failure: 'Unable to create scheduled edit: %{error}',
    },
    cancel: {
      button: 'Cancel',
      title: 'Cancel scheduled edit',
      content: 'This scheduled edit will be cancelled and will not be executed.',
      success: 'Scheduled edit cancelled',
      failure: 'Unable to cancel scheduled edit: %{error}',
    },
  },
};
